import * as THREE from "three";
import type {
  ControllerHandle,
  InputListener,
  InteractionSystem,
} from "../xr/input";
import type { XRRig } from "../xr/session";
import {
  restoreGrabbable,
  type Grabbable,
} from "./grabbable";
import {
  clearTarget,
  findBestTarget,
  isOccupiedBy,
  lockIntoTarget,
  releaseOccupant,
  type PlacementTarget,
} from "./placement";

export type PlaceOutcome =
  | { kind: "locked"; grabbable: Grabbable; target: PlacementTarget }
  | { kind: "rejected"; grabbable: Grabbable; target: PlacementTarget }
  | { kind: "dropped"; grabbable: Grabbable }
  | {
      kind: "unplugged";
      grabbable: Grabbable;
      target: PlacementTarget | null;
    };

export interface GrabManagerOptions {
  rig: XRRig;
  interaction: InteractionSystem;
  /** Max ray distance for XR/desktop grab picks (meters). */
  grabRayDistance?: number;
  /** Grip proximity grab radius (meters). */
  proximityRadius?: number;
  /** Desktop hold offset in camera-local space. */
  desktopHoldOffset?: THREE.Vector3;
  onPlaceOutcome?: (outcome: PlaceOutcome) => void;
}

interface HoldState {
  grabbable: Grabbable;
  /** Controller index, or -1 for desktop. */
  controllerIndex: number;
}

/**
 * Shared grab/ownership pipeline for controllers + desktop.
 *
 * Rules:
 * - One object held by at most one controller/hand (or desktop cursor).
 * - Squeeze (XR) or click (desktop) claims; release attempts placement.
 * - Session disconnect / {@link reset} force-releases without locking.
 *
 * Hand tracking / pinch is deferred — controller squeeze + desktop cover the
 * milestone path with a single ownership model.
 */
export class GrabManager {
  private readonly rig: XRRig;
  private readonly interaction: InteractionSystem;
  private readonly grabRayDistance: number;
  private readonly proximityRadius: number;
  private readonly desktopHoldOffset: THREE.Vector3;
  private readonly onPlaceOutcome?: (outcome: PlaceOutcome) => void;

  private readonly grabbables: Grabbable[] = [];
  private readonly targets: PlacementTarget[] = [];
  private readonly holds = new Map<number, HoldState>();
  private desktopHold: HoldState | null = null;

  private readonly raycaster = new THREE.Raycaster();
  private readonly _origin = new THREE.Vector3();
  private readonly _dir = new THREE.Vector3();
  private readonly _quat = new THREE.Quaternion();
  private readonly _gripPos = new THREE.Vector3();
  private readonly _objPos = new THREE.Vector3();
  private readonly _desktopPos = new THREE.Vector3();

  private readonly unbindListener: () => void;
  private readonly interactableCleanups: Array<() => void> = [];

  /** Transient feedback timers (seconds remaining). */
  private readonly pulses: Array<{
    mats: THREE.MeshStandardMaterial[];
    baseEmissive: number[];
    color: number;
    t: number;
  }> = [];

  constructor(opts: GrabManagerOptions) {
    this.rig = opts.rig;
    this.interaction = opts.interaction;
    this.grabRayDistance = opts.grabRayDistance ?? 1.5;
    this.proximityRadius = opts.proximityRadius ?? 0.12;
    this.desktopHoldOffset =
      opts.desktopHoldOffset?.clone() ?? new THREE.Vector3(0, -0.05, -0.45);
    this.onPlaceOutcome = opts.onPlaceOutcome;

    const listener: InputListener = {
      onSqueezeStart: (c) => this.onSqueezeStart(c),
      onSqueezeEnd: (c) => this.onSqueezeEnd(c),
      onControllerConnection: (c, connected) => {
        if (!connected) this.forceReleaseController(c.index);
      },
    };
    this.unbindListener = this.interaction.addListener(listener);
  }

  addGrabbable(grabbable: Grabbable): void {
    this.grabbables.push(grabbable);
    // Desktop: click toggles pick / drop for this object.
    this.interaction.register({
      object: grabbable.object,
      onSelect: () => this.onDesktopSelectGrabbable(grabbable),
      onHover: (hovered) => {
        if (!this.canPick(grabbable) && !this.isHeld(grabbable)) return;
        this.setHoverEmissive(grabbable.feedbackMats, hovered);
      },
    });
    this.interactableCleanups.push(() =>
      this.interaction.unregister(grabbable.object),
    );
  }

  addTarget(target: PlacementTarget): void {
    this.targets.push(target);
    // Desktop: click target while holding attempts placement.
    this.interaction.register({
      object: target.object,
      onSelect: () => this.onDesktopSelectTarget(target),
    });
    this.interactableCleanups.push(() =>
      this.interaction.unregister(target.object),
    );
  }

  getGrabbables(): readonly Grabbable[] {
    return this.grabbables;
  }

  getTargets(): readonly PlacementTarget[] {
    return this.targets;
  }

  isHeld(grabbable: Grabbable): boolean {
    if (this.desktopHold?.grabbable === grabbable) return true;
    for (const hold of this.holds.values()) {
      if (hold.grabbable === grabbable) return true;
    }
    return false;
  }

  /** Who owns this object, if anyone. */
  ownerOf(grabbable: Grabbable): number | "desktop" | null {
    if (this.desktopHold?.grabbable === grabbable) return "desktop";
    for (const [idx, hold] of this.holds) {
      if (hold.grabbable === grabbable) return idx;
    }
    return null;
  }

  /**
   * Force-drop every held object without snap/lock. Does not unlock already
   * placed objects or clear target occupancy — use {@link restoreAll} for that.
   */
  reset(): void {
    this.forceReleaseAll();
    this.clearPulses();
  }

  /** Full restore of every grabbable to rest (used by trainer reset). */
  restoreAll(enable?: (g: Grabbable) => boolean): void {
    this.forceReleaseAll();
    for (const t of this.targets) clearTarget(t);
    for (const g of this.grabbables) {
      restoreGrabbable(g, enable ? enable(g) : true);
      this.setHoverEmissive(g.feedbackMats, false);
    }
    this.clearPulses();
  }

  dispose(): void {
    this.forceReleaseAll();
    this.unbindListener();
    for (const cleanup of this.interactableCleanups) cleanup();
    this.interactableCleanups.length = 0;
    this.clearPulses();
  }

  update(dt: number): void {
    // Follow desktop hold in front of camera.
    if (this.desktopHold && !this.rig.renderer.xr.isPresenting) {
      const g = this.desktopHold.grabbable;
      const cam = this.rig.camera;
      this._desktopPos.copy(this.desktopHoldOffset);
      this._desktopPos.applyMatrix4(cam.matrixWorld);
      g.object.position.copy(this._desktopPos);
      cam.getWorldQuaternion(this._quat);
      g.object.quaternion.copy(this._quat);
    }

    // Tick feedback pulses without allocating.
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const p = this.pulses[i]!;
      p.t -= dt;
      const intensity = Math.max(0, p.t / 0.35);
      for (let m = 0; m < p.mats.length; m++) {
        const mat = p.mats[m]!;
        mat.emissive.setHex(p.color);
        mat.emissiveIntensity = p.baseEmissive[m]! + intensity * 0.85;
      }
      if (p.t <= 0) {
        for (let m = 0; m < p.mats.length; m++) {
          const mat = p.mats[m]!;
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = p.baseEmissive[m]!;
        }
        this.pulses.splice(i, 1);
      }
    }
  }

  // --- XR squeeze path ----------------------------------------------------

  private onSqueezeStart(controller: ControllerHandle): void {
    if (controller.owned) return;
    const hit = this.pickGrabbable(controller);
    if (!hit) return;
    if (!this.beginHold(hit, controller.index, controller)) return;
    // Preserve world pose — attach to grip (no grab jump).
    controller.grip.attach(hit.object);
    controller.owned = hit.object;
    this.pulseHaptic(controller, 0.25, 25);
  }

  private onSqueezeEnd(controller: ControllerHandle): void {
    const hold = this.holds.get(controller.index);
    if (!hold) {
      // Orphan token only — never steal RotationalControl / other owners.
      if (this.isOurOwnedToken(controller.owned)) controller.owned = null;
      return;
    }
    // Session already torn down (race with sessionend): never snap/lock.
    if (!this.rig.renderer.xr.isPresenting) {
      this.forceReleaseController(controller.index);
      return;
    }
    this.releaseHold(hold, controller);
  }

  // --- Desktop click path -------------------------------------------------

  private onDesktopSelectGrabbable(grabbable: Grabbable): void {
    if (this.rig.renderer.xr.isPresenting) return;

    // Clicking the held object drops it (no snap).
    if (this.desktopHold?.grabbable === grabbable) {
      this.dropDesktop(false);
      return;
    }

    if (this.desktopHold) {
      // Swap: drop current without place, then pick new.
      this.dropDesktop(false);
    }

    if (!this.beginHold(grabbable, -1)) return;
    grabbable.object.updateMatrixWorld(true);
    // Reparent to scene root so camera follow writes world pose cleanly.
    this.rig.scene.attach(grabbable.object);
  }

  private onDesktopSelectTarget(target: PlacementTarget): void {
    if (this.rig.renderer.xr.isPresenting) return;
    if (!this.desktopHold) return;
    this.attemptPlaceHold(this.desktopHold, target);
  }

  private dropDesktop(attemptPlace: boolean): void {
    const hold = this.desktopHold;
    if (!hold) return;
    this.desktopHold = null;
    if (attemptPlace) {
      this.attemptPlaceHold(hold, null);
    } else {
      // Leave where it is in world, reparent to rest parent.
      hold.grabbable.restParent.attach(hold.grabbable.object);
      this.onPlaceOutcome?.({ kind: "dropped", grabbable: hold.grabbable });
    }
  }

  // --- Shared claim / place / release -------------------------------------

  /** Eligible for a new claim (includes locked objects that unplug on grab). */
  private canPick(grabbable: Grabbable): boolean {
    if (this.ownerOf(grabbable) != null) return false;
    if (grabbable.locked) return grabbable.unplugOnGrab;
    return grabbable.enabled;
  }

  /**
   * Claim ownership, unplugging a seated end first when allowed.
   * Does not reparent — caller attaches to grip / scene.
   */
  private beginHold(
    grabbable: Grabbable,
    controllerIndex: number,
    controller?: ControllerHandle,
  ): boolean {
    if (!this.canPick(grabbable)) return false;
    // Double-ownership guard: controller already holding something.
    if (controllerIndex >= 0 && this.holds.has(controllerIndex)) return false;
    if (controllerIndex >= 0 && controller?.owned) return false;

    if (grabbable.locked && grabbable.unplugOnGrab) {
      const from = this.unplugIfLocked(grabbable);
      this.onPlaceOutcome?.({ kind: "unplugged", grabbable, target: from });
    }

    const hold: HoldState = { grabbable, controllerIndex };
    if (controllerIndex >= 0) this.holds.set(controllerIndex, hold);
    else this.desktopHold = hold;
    return true;
  }

  /** Clear occupancy and unlock so the object can be held. Pose is unchanged. */
  private unplugIfLocked(grabbable: Grabbable): PlacementTarget | null {
    let from: PlacementTarget | null = null;
    for (const t of this.targets) {
      if (isOccupiedBy(t, grabbable)) {
        // Release only this plug — stacked neighbors on jumper posts stay seated.
        releaseOccupant(t, grabbable);
        from = t;
        break;
      }
    }
    grabbable.locked = false;
    grabbable.enabled = true;
    return from;
  }

  private releaseHold(
    hold: HoldState,
    controller: ControllerHandle | null,
  ): void {
    if (hold.controllerIndex >= 0) this.holds.delete(hold.controllerIndex);
    if (controller) controller.owned = null;
    this.attemptPlaceHold(hold, null);
  }

  /**
   * Try preferred target (desktop click) or nearest in-range target (XR release).
   */
  private attemptPlaceHold(
    hold: HoldState,
    preferred: PlacementTarget | null,
  ): void {
    const { grabbable } = hold;
    const isDesktop = hold.controllerIndex < 0;

    // Desktop click names a target — ignore world distance (held at camera).
    const best = preferred
      ? findBestTarget(grabbable, [preferred], { ignoreDistance: true })
      : findBestTarget(grabbable, this.targets);

    if (best && best.result.ok) {
      // Clear hold bookkeeping before lock (object leaves hand/camera).
      if (isDesktop) this.desktopHold = null;
      lockIntoTarget(grabbable, best.target);
      this.pulseMats(best.target.feedbackMats, 0x35c46a);
      this.pulseMats(grabbable.feedbackMats, 0x35c46a);
      this.onPlaceOutcome?.({
        kind: "locked",
        grabbable,
        target: best.target,
      });
      return;
    }

    if (
      best &&
      !best.result.ok &&
      (best.result.reason === "wrong_target" ||
        best.result.reason === "occupied" ||
        best.result.reason === "invalid")
    ) {
      this.pulseMats(best.target.feedbackMats, 0xc04545);
      this.pulseMats(grabbable.feedbackMats, 0xc04545);
      // Desktop: keep holding for immediate retry. XR: drop in place.
      if (isDesktop) {
        this.desktopHold = hold;
        this.rig.scene.attach(grabbable.object);
      } else {
        grabbable.restParent.attach(grabbable.object);
      }
      this.onPlaceOutcome?.({
        kind: "rejected",
        grabbable,
        target: best.target,
      });
      return;
    }

    if (preferred && isDesktop) {
      // Occupied / gated click — keep holding.
      this.desktopHold = hold;
      this.rig.scene.attach(grabbable.object);
      return;
    }

    // Free drop — keep world pose under rest parent.
    if (isDesktop) this.desktopHold = null;
    grabbable.restParent.attach(grabbable.object);
    this.onPlaceOutcome?.({ kind: "dropped", grabbable });
  }

  private forceReleaseController(index: number): void {
    const hold = this.holds.get(index);
    if (!hold) {
      const c = this.interaction.getControllers()[index];
      // Clear only this manager's orphan tokens (multi-station safe).
      if (c && this.isOurOwnedToken(c.owned)) c.owned = null;
      return;
    }
    this.holds.delete(index);
    const c = this.interaction.getControllers()[index];
    if (c) c.owned = null;
    hold.grabbable.restParent.attach(hold.grabbable.object);
  }

  private forceReleaseAll(): void {
    for (const index of [...this.holds.keys()]) {
      this.forceReleaseController(index);
    }
    if (this.desktopHold) {
      const g = this.desktopHold.grabbable;
      this.desktopHold = null;
      g.restParent.attach(g.object);
    }
    // Do not null every controller.owned — V/I/R knob (and future stations)
    // share the same handles and must keep ownership across outlet-only resets.
  }

  /** True when `owned` points at one of this manager's grabbable roots. */
  private isOurOwnedToken(owned: THREE.Object3D | null): boolean {
    if (!owned) return false;
    for (const g of this.grabbables) {
      if (g.object === owned) return true;
    }
    return false;
  }

  private pickGrabbable(controller: ControllerHandle): Grabbable | null {
    // 1) Ray from target-ray against grabbable meshes.
    this._origin.set(0, 0, 0);
    this._dir.set(0, 0, -1);
    controller.targetRay.getWorldPosition(this._origin);
    controller.targetRay.getWorldQuaternion(this._quat);
    this._dir.applyQuaternion(this._quat);
    this.raycaster.set(this._origin, this._dir);
    this.raycaster.far = this.grabRayDistance;

    const meshes: THREE.Object3D[] = [];
    for (const g of this.grabbables) {
      if (!this.canPick(g)) continue;
      meshes.push(g.object);
    }
    if (meshes.length > 0) {
      const hits = this.raycaster.intersectObjects(meshes, true);
      if (hits.length > 0) {
        const found = this.grabbableFromHit(hits[0]!.object);
        if (found) return found;
      }
    }

    // 2) Proximity from grip.
    controller.grip.getWorldPosition(this._gripPos);
    let best: Grabbable | null = null;
    let bestDist = this.proximityRadius;
    for (const g of this.grabbables) {
      if (!this.canPick(g)) continue;
      g.object.getWorldPosition(this._objPos);
      const d = this._gripPos.distanceTo(this._objPos);
      if (d < bestDist) {
        bestDist = d;
        best = g;
      }
    }
    return best;
  }

  private grabbableFromHit(hit: THREE.Object3D): Grabbable | null {
    for (const g of this.grabbables) {
      let node: THREE.Object3D | null = hit;
      while (node) {
        if (node === g.object) return g;
        node = node.parent;
      }
    }
    return null;
  }

  private setHoverEmissive(
    mats: THREE.MeshStandardMaterial[],
    hovered: boolean,
  ): void {
    for (const mat of mats) {
      if (hovered) {
        mat.emissive.setHex(0x66ccff);
        mat.emissiveIntensity = Math.max(mat.emissiveIntensity, 0.2);
      } else {
        // Don't stomp an active pulse.
        if (this.pulses.some((p) => p.mats.includes(mat))) continue;
        mat.emissive.setHex(0x000000);
        mat.emissiveIntensity = 0;
      }
    }
  }

  private pulseMats(mats: THREE.MeshStandardMaterial[], color: number): void {
    if (mats.length === 0) return;
    const baseEmissive = mats.map((m) => m.emissiveIntensity);
    this.pulses.push({ mats, baseEmissive, color, t: 0.35 });
  }

  private clearPulses(): void {
    for (const p of this.pulses) {
      for (let m = 0; m < p.mats.length; m++) {
        const mat = p.mats[m]!;
        mat.emissive.setHex(0x000000);
        mat.emissiveIntensity = 0;
      }
    }
    this.pulses.length = 0;
  }

  private pulseHaptic(
    controller: ControllerHandle,
    intensity: number,
    durationMs: number,
  ): void {
    const session = this.rig.renderer.xr.getSession();
    if (!session) return;
    for (const source of session.inputSources) {
      if (source.targetRaySpace == null) continue;
      // Match by gamepad presence; both controllers may fire — keep subtle.
      const gamepad = source.gamepad as Gamepad & {
        hapticActuators?: ReadonlyArray<{ pulse(value: number, duration: number): Promise<boolean> }>;
      } | null;
      const actuator = gamepad?.hapticActuators?.[0];
      if (actuator?.pulse) {
        void actuator.pulse(intensity, durationMs);
        return;
      }
      const vibe = (
        gamepad as Gamepad & {
          vibrationActuator?: { playEffect: (type: string, opts: object) => Promise<unknown> };
        }
      )?.vibrationActuator;
      if (vibe?.playEffect) {
        void vibe.playEffect("dual-rumble", {
          duration: durationMs,
          strongMagnitude: intensity,
          weakMagnitude: intensity * 0.6,
        });
        return;
      }
    }
    void controller;
  }
}
